import React, { useEffect, useState } from 'react';
import { Heart, FileText, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { useToast } from '../hooks/use-toast';

const CIRCLE_CLS = { Benefactor: 'bg-burgundy text-cream', Patron: 'bg-mahogany text-cream', Supporter: 'bg-rose-soft text-burgundy', Friend: 'bg-white text-mahogany border border-rose/40' };
export const NAME_STYLES = [['first_initial', 'First name & last initial'], ['full', 'Full name'], ['initials', 'Initials only'], ['anonymous', 'Anonymous friend']];

export const WallOptIn = ({ wall, setWall }) => (
  <div className="mt-2 space-y-1.5" data-testid="wall-optin">
    <label className="flex items-center gap-2 text-xs text-burgundy cursor-pointer">
      <input type="checkbox" checked={wall.opt_in} onChange={(e) => setWall({ ...wall, opt_in: e.target.checked })} data-testid="wall-optin-checkbox" />
      Thank me on the supporters' wall (amounts are never shown)
    </label>
    {wall.opt_in && (
      <select value={wall.style} onChange={(e) => setWall({ ...wall, style: e.target.value })} data-testid="wall-name-style"
        className="w-full bg-white rounded-lg border border-rose/50 px-2 py-1.5 text-xs text-burgundy">
        {NAME_STYLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    )}
  </div>
);

export const DonorWall = ({ programId, title = 'With gratitude to our supporters' }) => {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(programId ? `/donor-wall/program/${programId}` : '/donor-wall').then((r) => setRows(r.data || [])).catch(() => {}); }, [programId]);
  if (!rows.length) return null;
  return (
    <section className="mt-12" data-testid="donor-wall">
      <h2 className="font-serif-display text-2xl text-burgundy flex items-center gap-2"><Heart className="w-5 h-5 text-rose-italic shrink-0" /> {title}</h2>
      <div className="mt-4 flex flex-wrap gap-2">
        {rows.map((r, i) => (
          <span key={i} className="inline-flex items-center gap-2 bg-white/70 rounded-full border border-rose/40 pl-3 pr-1 py-1 text-sm text-burgundy" data-testid={`donor-wall-item-${i}`}>
            {r.name}<span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${CIRCLE_CLS[r.circle]}`}>{r.circle}</span>
          </span>
        ))}
      </div>
    </section>
  );
};

export const GivingStatementCard = () => {
  const { toast } = useToast();
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear - 1);
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    const w = window.open('', '_blank');
    try { const r = await api.get(`/donations/statement/${year}`, { responseType: 'blob' }); w.location.href = URL.createObjectURL(r.data); }
    catch { w?.close(); toast({ title: `No gifts found for ${year}` }); } finally { setBusy(false); }
  };
  return (
    <section className="mt-10 bg-white/70 rounded-3xl border border-rose/40 p-6" data-testid="giving-statement-card">
      <h2 className="font-serif-display text-2xl text-burgundy flex items-center gap-2"><FileText className="w-5 h-5 text-rose-italic" /> Giving statement</h2>
      <p className="text-sm text-mahogany/70 mt-1">A yearly summary of your gifts for tax records (also emailed every January 15).</p>
      <div className="mt-3 flex items-center gap-2">
        <select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} className="bg-white rounded-xl border border-rose/50 px-3 py-2 text-sm text-burgundy" data-testid="giving-statement-year">
          {[0, 1, 2, 3].map((d) => <option key={d} value={thisYear - d}>{thisYear - d}</option>)}
        </select>
        <button onClick={open} disabled={busy} className="pill-btn-outline text-sm" data-testid="giving-statement-open">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} View statement</button>
      </div>
    </section>
  );
};
